import React, { useCallback, useContext, useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ImageConfigProps, ImageConfigValue } from './types';
import LibraryTitleInput from '../library-title-input';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { DeleteIcon, UploadIcon } from '../../../bombiix-icon';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import { NewInput } from '../../PIM/common/NewInput';
import FieldSetupPanelContext from './field-setup-panel.context';
import CmPanelTabs from '../cm-panel-tabs';
import { PositionCentreIcon, PositionLeftIcon, PositionRightIcon } from '../../../bombiix-icon';
import type { BombiixIconProps } from '../../../bombiix-icon/types';

const ImageConfig: React.FC<ImageConfigProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  showLibraryTitle = true,
  width,
  onWidthChange,
  widthError,
  hideTitle = false,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const setupPanel = useContext(FieldSetupPanelContext);
  const isInSetupCard = setupPanel !== null;
  const showCardOptions = setupPanel === 'options';
  const titlePlaceholder = t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER');
  const showWidthRow = onWidthChange != null;

  const normalizedValue: ImageConfigValue = React.useMemo(
    () => ({
      title: value?.title ?? '',
      required: value?.required ?? false,
      size: value?.size ?? 'medium',
      orientation: value?.orientation ?? 'square',
      position: value?.position ?? 'left',
    }),
    [value],
  );

  const [localValue, setLocalValue] = useState<ImageConfigValue>(normalizedValue);
  const [libraryTitle, setLibraryTitle] = useState<string>('');

  useEffect(() => {
    setLocalValue(normalizedValue);
  }, [normalizedValue]);

  const updateTitle = useCallback(
    (next: string) => {
      setLocalValue((prev: ImageConfigValue) => {
        const nextVal = { ...prev, title: next };
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const toggleRequired = useCallback(() => {
    setLocalValue((prev: ImageConfigValue) => {
      const nextVal = { ...prev, required: !prev.required };
      onChange(nextVal);
      return nextVal;
    });
  }, [onChange]);

  const handleSizeChange = useCallback(
    (size: 'small' | 'medium' | 'large') => {
      setLocalValue((prev: ImageConfigValue) => {
        const nextVal = { ...prev, size };
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: unknown) => {
      const fetchedConfig = (controlTypeData as { content?: { content?: ImageConfigValue } })?.content?.content as ImageConfigValue | undefined;
      if (fetchedConfig) {
        setLocalValue(fetchedConfig);
        onChange(fetchedConfig);
      }
    },
    [onChange],
  );

  const orientationTabs = [
    { key: 'square' as const, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.SQUARE') },
    { key: 'portrait' as const, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.PORTRAIT') },
    { key: 'landscape' as const, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.LANDSCAPE') },
  ];

  const imagePreview = (
    <div className="mb-2 mt-2 cm-image-control cm-image-control--left">
      <div className={`image-upload-container image-size-${localValue.size || 'medium'} cm-image-control__frame--${localValue.orientation || 'square'}`}>
        <figure className="mb-0">
          <span className={`img-thumb img-size-${localValue.size || 'medium'} cm-image-control__placeholder`} aria-hidden="true" />
          <div className="btn-container">
            <button type="button" className="btn cm-image-control__btn cm-image-control__btn--delete" disabled={disabled}>
              <span className="sr-only">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.DELETE_IMAGE')}</span>
              <DeleteIcon size={IMAGE_ACTION_ICON_SIZE} />
            </button>
            <button type="button" className="btn cm-image-control__btn cm-image-control__btn--upload" disabled={disabled}>
              <span className="sr-only">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.UPLOAD_IMAGE')}</span>
              <UploadIcon size={IMAGE_ACTION_ICON_SIZE} />
            </button>
          </div>
        </figure>
      </div>
    </div>
  );

  /**
   * Applies an Orientation, Position or Size change from the Options tab.
   * @param patch - The changed option.
   */
  const updateImageOption = (patch: Partial<Pick<ImageConfigValue, 'orientation' | 'position' | 'size'>>) => {
    const nextVal = { ...localValue, ...patch };
    setLocalValue(nextVal);
    onChange(nextVal);
  };

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && (
        <div className="mb-2">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.TITLE')}</p>
          <NewInput
            type="text"
            placeholder={titlePlaceholder}
            value={localValue.title}
            onChange={(e) => updateTitle(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="image"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      {!isInSetupCard && (
      <div className="mb-1 mt-2 d-flex align-items-center cm-checkbox">
        <label className="m-0 mr-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')}</label>
        {!localValue.title?.trim() ? (
          <CustomTooltip tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
            <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled || !localValue.title?.trim()} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
          </CustomTooltip>
        ) : (
          <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
        )}
      </div>
      )}

      {!showWidthRow && !hideTitle && (
        <div className="mb-2">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.TITLE')}</p>
          <NewInput
            type="text"
            placeholder={titlePlaceholder}
            value={localValue.title}
            onChange={(e) => updateTitle(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}

      {showCardOptions && (
        <div className="cm-image-options">
          <div className="cm-field-setup__field">
            <span className="cm-field-setup__field-label">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.ORIENTATION')}</span>
            <CmPanelTabs
              className="cm-panel-tabs--filled"
              tabs={orientationTabs}
              activeKey={localValue.orientation ?? 'square'}
              onChange={(orientation) => updateImageOption({ orientation })}
              ariaLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.ORIENTATION')}
            />
          </div>
          <div className="cm-image-options__row">
            <span className="cm-image-options__box">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.POSITION')}</span>
            <div className="cm-image-options__positions" role="group" aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.POSITION')}>
              {IMAGE_POSITIONS.map(({ key, Icon, labelKey }) => {
                const isActive = (localValue.position ?? 'left') === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={['cm-image-options__position', isActive ? 'cm-image-options__position--active' : ''].filter(Boolean).join(' ')}
                    aria-pressed={isActive}
                    aria-label={t(labelKey)}
                    onClick={() => updateImageOption({ position: key })}
                    disabled={disabled}
                  >
                    <Icon size={POSITION_ICON_SIZE} />
                  </button>
                );
              })}
            </div>
          </div>
          <label className="cm-image-options__box cm-image-options__size">
            <span>{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.SIZE')}</span>
            <input
              type="range"
              className="cm-field-setup__slider"
              min={0}
              max={IMAGE_SIZES.length - 1}
              step={1}
              value={Math.max(IMAGE_SIZES.indexOf(localValue.size ?? 'medium'), 0)}
              onChange={(event) => updateImageOption({ size: IMAGE_SIZES[Number(event.target.value)] ?? 'medium' })}
              aria-valuetext={localValue.size ?? 'medium'}
              disabled={disabled}
            />
          </label>
          {imagePreview}
        </div>
      )}
      {!isInSetupCard && (
      <div className="mb-2">
        <p className="m-0 mb-2">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.SIZE')}</p>
        <div className="px-2 mt-2">
          <div className="cm-image-size-tabs" role="tablist" aria-label="Image Size Selection">
            <button
              type="button"
              role="tab"
              aria-selected={localValue.size === 'small'}
              className={`cm-image-size-tab ${localValue.size === 'small' ? 'cm-image-size-tab--active' : ''}`}
              onClick={() => handleSizeChange('small')}
              disabled={disabled}
            >
              {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.SIZE_SMALL')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={localValue.size === 'medium' || !localValue.size}
              className={`cm-image-size-tab ${localValue.size === 'medium' || !localValue.size ? 'cm-image-size-tab--active' : ''}`}
              onClick={() => handleSizeChange('medium')}
              disabled={disabled}
            >
              {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.SIZE_MEDIUM')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={localValue.size === 'large'}
              className={`cm-image-size-tab ${localValue.size === 'large' ? 'cm-image-size-tab--active' : ''}`}
              onClick={() => handleSizeChange('large')}
              disabled={disabled}
            >
              {t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.SIZE_LARGE')}
            </button>
          </div>
        </div>
      </div>
      )}
      {!isInSetupCard && imagePreview}
    </div>
  );
};

export default memo(ImageConfig);

const POSITION_ICON_SIZE = 20;

const IMAGE_SIZES: ReadonlyArray<'small' | 'medium' | 'large'> = ['small', 'medium', 'large'];

const IMAGE_POSITIONS: ReadonlyArray<{ key: 'left' | 'center' | 'right'; Icon: React.FC<BombiixIconProps>; labelKey: string }> = [
  { key: 'left', Icon: PositionLeftIcon, labelKey: 'CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.POSITION_LEFT' },
  { key: 'center', Icon: PositionCentreIcon, labelKey: 'CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.POSITION_CENTRE' },
  { key: 'right', Icon: PositionRightIcon, labelKey: 'CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.IMAGE.POSITION_RIGHT' },
];

const IMAGE_ACTION_ICON_SIZE = 16;
